import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import {
  PlusIcon,
  TrashIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  PhotoIcon,
  DocumentTextIcon,
  CodeBracketIcon,
  ArrowLeftIcon,
  EyeIcon,
  ArchiveBoxIcon,
  EyeSlashIcon,
  MegaphoneIcon,
} from '@heroicons/react/24/outline';
import AnnouncementService from './AnnouncementService';

const uploadInlineImage = async (file) => {
  const formData = new FormData();
  formData.append('image', file);
  formData.append('noResize', 'true');
  const res = await axios.post(
    `${process.env.REACT_APP_BACKEND_URL}/api/cloudinary/manual-upload`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  );
  return res.data.url;
};

const EMPTY_BLOCK = (type = 'text') => ({ type, content: '' });

const STATUS_CONFIG = {
  draft:     { label: 'ร่าง',     color: '#ca8a04', bg: '#fef9c3', border: '#fde047' },
  published: { label: 'เผยแพร่', color: '#059669', bg: '#f0fdf4', border: '#6ee7b7' },
  hidden:    { label: 'ซ่อน',    color: '#6b7280', bg: '#f3f4f6', border: '#d1d5db' },
};

const BlockEditor = ({ block, index, total, onChange, onRemove, onMoveUp, onMoveDown }) => {
  const isText = block.type === 'text';
  const isHtml = block.type === 'html';
  const [uploading, setUploading] = useState(false);

  const handleImagePaste = async (e) => {
    const file = Array.from(e.clipboardData.files).find(f => f.type.startsWith('image/'));
    if (!file) return;
    e.preventDefault();
    setUploading(true);
    try {
      const url = await uploadInlineImage(file);
      onChange(index, { ...block, content: url });
    } catch {
      toast.error('อัปโหลดรูปภาพล้มเหลว');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div style={{
      border: '1px solid #e5e7eb',
      borderRadius: '10px',
      marginBottom: '12px',
      overflow: 'hidden',
      background: '#fff'
    }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '8px 12px',
        background: isText ? '#f0fdf4' : isHtml ? '#faf5ff' : '#eff6ff',
        borderBottom: '1px solid #e5e7eb'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          flex: 1,
          fontSize: '13px',
          fontWeight: '600',
          color: isText ? '#065f46' : isHtml ? '#6b21a8' : '#1e40af'
        }}>
          {isText
            ? <DocumentTextIcon style={{ width: 16, height: 16 }} />
            : isHtml
            ? <CodeBracketIcon style={{ width: 16, height: 16 }} />
            : <PhotoIcon style={{ width: 16, height: 16 }} />
          }
          {isText ? `บล็อคข้อความ #${index + 1}` : isHtml ? `บล็อค HTML #${index + 1}` : `บล็อครูปภาพ #${index + 1}`}
        </div>
        <div style={{ display: 'flex', gap: '4px' }}>
          <button
            onClick={() => onMoveUp(index)}
            disabled={index === 0}
            style={{
              background: 'none', border: '1px solid #d1d5db', borderRadius: '4px',
              padding: '3px 6px', cursor: index === 0 ? 'not-allowed' : 'pointer',
              opacity: index === 0 ? 0.4 : 1, color: '#374151'
            }}
            title="เลื่อนขึ้น"
          >
            <ArrowUpIcon style={{ width: 12, height: 12 }} />
          </button>
          <button
            onClick={() => onMoveDown(index)}
            disabled={index === total - 1}
            style={{
              background: 'none', border: '1px solid #d1d5db', borderRadius: '4px',
              padding: '3px 6px', cursor: index === total - 1 ? 'not-allowed' : 'pointer',
              opacity: index === total - 1 ? 0.4 : 1, color: '#374151'
            }}
            title="เลื่อนลง"
          >
            <ArrowDownIcon style={{ width: 12, height: 12 }} />
          </button>
          <button
            onClick={() => onRemove(index)}
            style={{
              background: 'none', border: '1px solid #fca5a5', borderRadius: '4px',
              padding: '3px 6px', cursor: 'pointer', color: '#dc2626'
            }}
            title="ลบบล็อค"
          >
            <TrashIcon style={{ width: 12, height: 12 }} />
          </button>
        </div>
      </div>

      <div style={{ padding: '12px' }}>
        {isText ? (
          <textarea
            value={block.content}
            onChange={(e) => onChange(index, { ...block, content: e.target.value })}
            placeholder="พิมพ์เนื้อหาที่นี่..."
            rows={4}
            style={{
              width: '100%', border: '1px solid #d1d5db', borderRadius: '6px',
              padding: '8px 12px', fontSize: '14px', resize: 'vertical',
              boxSizing: 'border-box', fontFamily: 'inherit', lineHeight: '1.6'
            }}
          />
        ) : isHtml ? (
          <div>
            <textarea
              value={block.content}
              onChange={(e) => onChange(index, { ...block, content: e.target.value })}
              placeholder="วางโค้ด HTML ที่นี่ (เช่น จากไฟล์ patch note)"
              rows={6}
              style={{
                width: '100%', border: '1px solid #d1d5db', borderRadius: '6px',
                padding: '8px 12px', fontSize: '13px', resize: 'vertical',
                boxSizing: 'border-box', fontFamily: 'monospace', lineHeight: '1.5'
              }}
            />
            {block.content && (
              <div style={{ marginTop: '8px' }}>
                <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>
                  ตัวอย่าง (แสดงแบบปลอดภัยใน sandbox):
                </div>
                <iframe
                  title={`html-preview-${index}`}
                  sandbox=""
                  srcDoc={block.content}
                  style={{ width: '100%', height: '300px', border: '1px solid #e5e7eb', borderRadius: '6px', background: '#fff' }}
                />
              </div>
            )}
          </div>
        ) : (
          <div>
            <input
              type="text"
              value={uploading ? '' : block.content}
              onChange={(e) => onChange(index, { ...block, content: e.target.value })}
              onPaste={handleImagePaste}
              placeholder={uploading ? 'กำลังอัปโหลด...' : 'วาง URL หรือ Ctrl+V รูปภาพที่นี่'}
              disabled={uploading}
              style={{
                width: '100%', border: `1px solid ${uploading ? '#93c5fd' : '#d1d5db'}`, borderRadius: '6px',
                padding: '8px 12px', fontSize: '14px', boxSizing: 'border-box',
                background: uploading ? '#eff6ff' : undefined,
                color: uploading ? '#1e40af' : undefined,
              }}
            />
            {block.content && !uploading && (
              <div style={{ marginTop: '8px', textAlign: 'center' }}>
                <img
                  src={block.content}
                  alt="preview"
                  style={{ maxWidth: '100%', maxHeight: '200px', borderRadius: '6px', border: '1px solid #e5e7eb', objectFit: 'contain' }}
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const PreviewRenderer = ({ title, blocks }) => (
  <div style={{ border: '2px solid #3b82f6', borderRadius: '12px', overflow: 'hidden', background: '#fff' }}>
    <div style={{ background: 'linear-gradient(135deg, #eff6ff 0%, #f0fdf4 100%)', padding: '16px 20px', borderBottom: '1px solid #e5e7eb' }}>
      <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '700', color: '#111827' }}>
        {title || '(ยังไม่มีหัวข้อ)'}
      </h3>
    </div>
    <div style={{ padding: '20px 24px' }}>
      {blocks.length === 0 ? (
        <p style={{ color: '#9ca3af', fontStyle: 'italic' }}>ยังไม่มีเนื้อหา</p>
      ) : blocks.map((block, idx) => {
        if (block.type === 'image') {
          return block.content ? (
            <div key={idx} style={{ margin: '8px 0' }}>
              <img src={block.content} alt="" style={{ maxWidth: '100%', borderRadius: '8px' }} onError={(e) => { e.target.style.display = 'none'; }} />
            </div>
          ) : (
            <div key={idx} style={{ background: '#f3f4f6', borderRadius: '8px', padding: '20px', textAlign: 'center', color: '#9ca3af', margin: '8px 0' }}>
              [รูปภาพยังไม่มี URL]
            </div>
          );
        }
        if (block.type === 'html') {
          return block.content ? (
            <iframe key={idx} title={`html-preview-${idx}`} sandbox="" srcDoc={block.content}
              style={{ width: '100%', height: '300px', border: '1px solid #e5e7eb', borderRadius: '8px', margin: '8px 0', background: '#fff' }} />
          ) : (
            <div key={idx} style={{ background: '#f3f4f6', borderRadius: '8px', padding: '20px', textAlign: 'center', color: '#9ca3af', margin: '8px 0' }}>
              [HTML ยังว่าง]
            </div>
          );
        }
        return (
          <p key={idx} style={{ margin: '8px 0', lineHeight: '1.7', whiteSpace: 'pre-wrap', color: '#374151' }}>
            {block.content || <em style={{ color: '#9ca3af' }}>[ข้อความว่าง]</em>}
          </p>
        );
      })}
    </div>
  </div>
);

const AnnouncementCreate = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const editingAnnouncement = location.state?.editing || null;
  const isEditing = !!editingAnnouncement;

  const [title, setTitle] = useState('');
  const [blocks, setBlocks] = useState([EMPTY_BLOCK('text')]);
  const [isPinned, setIsPinned] = useState(false);
  const [currentStatus, setCurrentStatus] = useState('published');
  const [saving, setSaving] = useState(null); // null | 'draft' | 'published' | 'hidden'
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    if (editingAnnouncement) {
      const loadFull = async () => {
        try {
          const res = await AnnouncementService.getAnnouncement(editingAnnouncement.id);
          const data = res.data;
          setTitle(data.title || '');
          setBlocks(data.blocks && data.blocks.length > 0 ? data.blocks : [EMPTY_BLOCK('text')]);
          setIsPinned(!!data.isPinned);
          setCurrentStatus(data.status || 'published');
        } catch {
          setTitle(editingAnnouncement.title || '');
          setBlocks(editingAnnouncement.blocks || [EMPTY_BLOCK('text')]);
          setIsPinned(!!editingAnnouncement.isPinned);
          setCurrentStatus(editingAnnouncement.status || 'published');
        }
      };
      loadFull();
    }
  }, [editingAnnouncement]);

  const validateForm = () => {
    if (!title.trim()) { toast.error('กรุณาใส่หัวข้อประกาศ'); return null; }
    const validBlocks = blocks.filter(b => b.content.trim() !== '');
    if (validBlocks.length === 0) { toast.error('กรุณาใส่เนื้อหาอย่างน้อย 1 บล็อค'); return null; }
    return validBlocks;
  };

  const handleSaveAs = async (targetStatus) => {
    const validBlocks = validateForm();
    if (!validBlocks) return;

    setSaving(targetStatus);
    try {
      const payload = { title: title.trim(), blocks: validBlocks, isPinned, status: targetStatus };
      if (isEditing) {
        await AnnouncementService.updateAnnouncement(editingAnnouncement.id, payload);
        const statusLabel = STATUS_CONFIG[targetStatus]?.label || targetStatus;
        toast.success(
          targetStatus === currentStatus
            ? 'บันทึกสำเร็จ'
            : `เปลี่ยนสถานะเป็น "${statusLabel}" สำเร็จ`
        );
      } else {
        if (targetStatus === 'draft') {
          await AnnouncementService.createDraft(payload);
        } else {
          await AnnouncementService.createAnnouncement(payload);
        }
        toast.success(
          targetStatus === 'published' ? 'สร้างประกาศสำเร็จ' : `บันทึกเป็น "${STATUS_CONFIG[targetStatus]?.label}" สำเร็จ`
        );
      }
      navigate('/notifications');
    } catch (err) {
      toast.error(err?.response?.data?.message || 'บันทึกล้มเหลว');
    } finally {
      setSaving(null);
    }
  };

  const addBlock = (type) => setBlocks(prev => [...prev, EMPTY_BLOCK(type)]);
  const removeBlock = (index) => {
    if (blocks.length === 1) { toast.warning('ต้องมีอย่างน้อย 1 บล็อค'); return; }
    setBlocks(prev => prev.filter((_, i) => i !== index));
  };
  const updateBlock = (index, updated) => setBlocks(prev => prev.map((b, i) => (i === index ? updated : b)));
  const moveBlockUp = (index) => {
    if (index === 0) return;
    setBlocks(prev => { const arr = [...prev]; [arr[index - 1], arr[index]] = [arr[index], arr[index - 1]]; return arr; });
  };
  const moveBlockDown = (index) => {
    if (index === blocks.length - 1) return;
    setBlocks(prev => { const arr = [...prev]; [arr[index], arr[index + 1]] = [arr[index + 1], arr[index]]; return arr; });
  };

  const isBusy = saving !== null;
  const sc = STATUS_CONFIG[currentStatus] || STATUS_CONFIG.published;

  return (
    <div style={{ maxWidth: '860px', margin: '0 auto', padding: '24px 16px' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '28px' }}>
        <button
          onClick={() => navigate('/notifications')}
          style={{ background: 'none', border: '1px solid #d1d5db', borderRadius: '8px', padding: '8px', cursor: 'pointer', color: '#374151', display: 'flex', alignItems: 'center' }}
        >
          <ArrowLeftIcon style={{ width: 18, height: 18 }} />
        </button>
        <h1 style={{ margin: 0, fontSize: '20px', fontWeight: '700', color: '#111827', display: 'flex', alignItems: 'center', gap: '8px' }}>
          {isEditing ? 'แก้ไขประกาศ' : 'สร้างประกาศใหม่'}
          {isEditing && (
            <span style={{
              fontSize: '11px',
              background: sc.bg,
              color: sc.color,
              border: `1px solid ${sc.border}`,
              padding: '2px 10px',
              borderRadius: '20px',
              fontWeight: '700'
            }}>
              {sc.label}
            </span>
          )}
        </h1>
        <button
          onClick={() => setShowPreview(p => !p)}
          style={{
            marginLeft: 'auto',
            display: 'flex', alignItems: 'center', gap: '6px',
            background: showPreview ? '#6366f1' : '#fff',
            color: showPreview ? '#fff' : '#374151',
            border: '1px solid #d1d5db', borderRadius: '8px',
            padding: '8px 14px', fontSize: '13px', fontWeight: '600', cursor: 'pointer'
          }}
        >
          <EyeIcon style={{ width: 16, height: 16 }} />
          {showPreview ? 'ซ่อน Preview' : 'Preview'}
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: showPreview ? '1fr 1fr' : '1fr', gap: '24px' }}>
        <div>
          {/* Title */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', fontWeight: '600', fontSize: '14px', marginBottom: '6px', color: '#374151' }}>
              หัวข้อประกาศ *
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="เช่น แจ้งปิดระบบวันอาทิตย์"
              style={{ width: '100%', border: '1px solid #d1d5db', borderRadius: '8px', padding: '10px 14px', fontSize: '15px', boxSizing: 'border-box', fontWeight: '600' }}
            />
          </div>

          {/* Pin toggle */}
          <div style={{ marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <input
              type="checkbox"
              id="isPinned"
              checked={isPinned}
              onChange={(e) => setIsPinned(e.target.checked)}
              style={{ width: 16, height: 16, cursor: 'pointer' }}
            />
            <label htmlFor="isPinned" style={{ fontSize: '14px', color: '#374151', cursor: 'pointer' }}>
              📌 ปักหมุดประกาศนี้ไว้ด้านบน
            </label>
          </div>

          {/* Blocks */}
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', fontWeight: '600', fontSize: '14px', marginBottom: '10px', color: '#374151' }}>
              เนื้อหาประกาศ
              <span style={{ fontWeight: '400', color: '#6b7280', marginLeft: '8px', fontSize: '12px' }}>
                (ลากสลับลำดับได้ด้วยปุ่มลูกศร)
              </span>
            </label>
            {blocks.map((block, idx) => (
              <BlockEditor
                key={idx}
                block={block}
                index={idx}
                total={blocks.length}
                onChange={updateBlock}
                onRemove={removeBlock}
                onMoveUp={moveBlockUp}
                onMoveDown={moveBlockDown}
              />
            ))}
          </div>

          {/* Add block buttons */}
          <div style={{ display: 'flex', gap: '10px', marginBottom: '28px' }}>
            <button
              onClick={() => addBlock('text')}
              style={{ display: 'flex', alignItems: 'center', gap: '6px', background: '#f0fdf4', color: '#065f46', border: '1px dashed #6ee7b7', borderRadius: '8px', padding: '8px 16px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', flex: 1 }}
            >
              <PlusIcon style={{ width: 16, height: 16 }} />
              <DocumentTextIcon style={{ width: 16, height: 16 }} />
              เพิ่มบล็อคข้อความ
            </button>
            <button
              onClick={() => addBlock('image')}
              style={{ display: 'flex', alignItems: 'center', gap: '6px', background: '#eff6ff', color: '#1e40af', border: '1px dashed #93c5fd', borderRadius: '8px', padding: '8px 16px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', flex: 1 }}
            >
              <PlusIcon style={{ width: 16, height: 16 }} />
              <PhotoIcon style={{ width: 16, height: 16 }} />
              เพิ่มบล็อครูปภาพ
            </button>
            <button
              onClick={() => addBlock('html')}
              style={{ display: 'flex', alignItems: 'center', gap: '6px', background: '#faf5ff', color: '#6b21a8', border: '1px dashed #d8b4fe', borderRadius: '8px', padding: '8px 16px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', flex: 1 }}
            >
              <PlusIcon style={{ width: 16, height: 16 }} />
              <CodeBracketIcon style={{ width: 16, height: 16 }} />
              เพิ่มบล็อค HTML
            </button>
          </div>

          {/* Action buttons */}
          <div style={{ display: 'flex', gap: '8px' }}>
            {/* Save as Draft */}
            <button
              onClick={() => handleSaveAs('draft')}
              disabled={isBusy}
              title="บันทึกโดยไม่แจ้งเตือน — มองเห็นเฉพาะ Superuser"
              style={{
                flex: 1,
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                background: saving === 'draft' ? '#fef08a' : '#fefce8',
                color: '#713f12',
                border: '1px solid #fde047',
                borderRadius: '10px',
                padding: '11px',
                fontSize: '13px', fontWeight: '600',
                cursor: isBusy ? 'not-allowed' : 'pointer',
                opacity: isBusy && saving !== 'draft' ? 0.5 : 1
              }}
            >
              <ArchiveBoxIcon style={{ width: 16, height: 16 }} />
              {saving === 'draft' ? 'กำลังบันทึก...' : 'ร่าง'}
            </button>

            {/* Publish */}
            <button
              onClick={() => handleSaveAs('published')}
              disabled={isBusy}
              title="เผยแพร่ให้ทุกคนเห็นและรับแจ้งเตือน"
              style={{
                flex: 2,
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                background: saving === 'published' ? '#34d399' : '#10b981',
                color: '#fff',
                border: 'none',
                borderRadius: '10px',
                padding: '11px',
                fontSize: '14px', fontWeight: '700',
                cursor: isBusy ? 'not-allowed' : 'pointer',
                opacity: isBusy && saving !== 'published' ? 0.5 : 1
              }}
            >
              <MegaphoneIcon style={{ width: 17, height: 17 }} />
              {saving === 'published'
                ? 'กำลังเผยแพร่...'
                : currentStatus === 'draft' || currentStatus === 'hidden'
                  ? 'เผยแพร่'
                  : isEditing ? 'บันทึกการแก้ไข' : 'เผยแพร่ประกาศ'
              }
            </button>

            {/* Hide — only when editing a published announcement */}
            {isEditing && currentStatus === 'published' && (
              <button
                onClick={() => handleSaveAs('hidden')}
                disabled={isBusy}
                title="ซ่อนจากผู้ใช้ทั่วไป — Superuser ยังมองเห็น"
                style={{
                  flex: 1,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                  background: saving === 'hidden' ? '#e5e7eb' : '#f9fafb',
                  color: '#374151',
                  border: '1px solid #d1d5db',
                  borderRadius: '10px',
                  padding: '11px',
                  fontSize: '13px', fontWeight: '600',
                  cursor: isBusy ? 'not-allowed' : 'pointer',
                  opacity: isBusy && saving !== 'hidden' ? 0.5 : 1
                }}
              >
                <EyeSlashIcon style={{ width: 16, height: 16 }} />
                {saving === 'hidden' ? 'กำลังซ่อน...' : 'ซ่อน'}
              </button>
            )}
          </div>

          {/* Status legend */}
          <div style={{ marginTop: '14px', padding: '10px 14px', background: '#f9fafb', borderRadius: '8px', border: '1px solid #f3f4f6' }}>
            <div style={{ fontSize: '11px', color: '#6b7280', lineHeight: '1.8' }}>
              <span style={{ fontWeight: '700', color: '#374151' }}>ร่าง</span> — บันทึกไว้ก่อน มองเห็นได้เฉพาะ Superuser &nbsp;·&nbsp;
              <span style={{ fontWeight: '700', color: '#374151' }}>เผยแพร่</span> — แจ้งเตือนทุกคนและแสดงในรายการ &nbsp;·&nbsp;
              <span style={{ fontWeight: '700', color: '#374151' }}>ซ่อน</span> — หยุดแสดงโดยไม่ลบ
            </div>
          </div>
        </div>

        {/* Preview Column */}
        {showPreview && (
          <div>
            <label style={{ display: 'block', fontWeight: '600', fontSize: '14px', marginBottom: '10px', color: '#374151' }}>
              ตัวอย่าง
            </label>
            <PreviewRenderer title={title} blocks={blocks} />
          </div>
        )}
      </div>
    </div>
  );
};

export default AnnouncementCreate;
